import React, { useState } from 'react';
import { FaCheckCircle, FaBuilding, FaIdCard, FaMapMarkerAlt, FaEnvelope, FaPhone, FaUser, FaArrowRight, FaSpinner, FaSignInAlt } from 'react-icons/fa';
import parse from 'html-react-parser';
import { login } from '../../../services/user/auth.service';
import { useAuth } from '../../../context/auth-provider';
import { useNavigate } from 'react-router-dom';
import { TENANT } from '../../../constants/localstorage';

const B2BOnboardingRenderer = ({ message }) => {
  const { syncAuth } = useAuth();
  const navigate = useNavigate();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState(null);

  // Parse the message to extract structured data
  const parseB2BResponse = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const sections = {
      title: '',
      email: '',
      personalInfo: {},
      companyInfo: {},
      optionalData: {},
      nextSteps: {
        mandatory: [],
        optional: []
      },
      aiMessage: '',
      status: 'info'
    };

    let currentSection = 'title';
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      
      // Detect sections and handle bold text - flexible order
      if (trimmedLine.includes('Email:') || trimmedLine.includes('Business Email:')) {
        sections.email = trimmedLine.split(':')[1]?.trim().replace(/\*\*/g, '') || '';
      } else if (trimmedLine.includes('First Name:')) {
        sections.personalInfo.firstName = trimmedLine.split(':')[1]?.trim().replace(/\*\*/g, '') || '';
      } else if (trimmedLine.includes('Last Name:')) {
        sections.personalInfo.lastName = trimmedLine.split(':')[1]?.trim().replace(/\*\*/g, '') || '';
      } else if (trimmedLine.includes('Phone Number:') || trimmedLine.includes('Contact Phone:')) {
        sections.personalInfo.phone = trimmedLine.split(':')[1]?.trim().replace(/\*\*/g, '') || '';
      } else if (trimmedLine.includes('Company Name:') || trimmedLine.includes('Company:')) {
        sections.companyInfo.name = trimmedLine.split(':')[1]?.trim().replace(/\*\*/g, '') || '';
      } else if (trimmedLine.includes('VAT-ID:')) {
        sections.companyInfo.vatId = trimmedLine.split(':')[1]?.trim().replace(/\*\*/g, '') || '';
      } else if (trimmedLine.includes('Address:') || trimmedLine.includes('Company Address:')) {
        sections.companyInfo.address = trimmedLine.split(':')[1]?.trim().replace(/\*\*/g, '') || '';
      } else if (trimmedLine.includes('Next steps:') || trimmedLine.includes('Next Steps:')) {
        currentSection = 'nextSteps';
      } else if (trimmedLine.includes('Please confirm') || trimmedLine.includes('And provide')) {
        currentSection = 'actions';
      } else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('• ')) {
        if (currentSection === 'nextSteps') {
          const step = trimmedLine.substring(2).replace(/\*\*/g, '');
          if (step.toLowerCase().includes('optional') || step.toLowerCase().includes('skip')) {
            sections.nextSteps.optional.push(step);
          } else {
            sections.nextSteps.mandatory.push(step);
          }
        }
      } else if (trimmedLine.includes('✅') || trimmedLine.includes('verified')) {
        sections.status = 'success';
      } else if (trimmedLine.includes('❌') || trimmedLine.includes('error')) {
        sections.status = 'error';
      } else if (trimmedLine && !trimmedLine.includes(':') && currentSection === 'title') {
        sections.title = trimmedLine.replace(/\*\*/g, '');
      }
    }


    // Set AI message as the full text if no specific sections found
    if (!sections.email && !sections.personalInfo.firstName && !sections.companyInfo.name) {
      sections.aiMessage = text;
    }

    return sections;
  };

  const parsedData = parseB2BResponse(message.text);

  // Determine progress based on collected data
  const getProgressSteps = () => {
    const steps = [
      { id: 'email', label: 'Email', completed: !!parsedData.email },
      { id: 'personal', label: 'Personal Data', completed: !!(parsedData.personalInfo.firstName && parsedData.personalInfo.lastName) },
      { id: 'company', label: 'Company Data', completed: !!(parsedData.companyInfo.name && parsedData.companyInfo.vatId) },
      { id: 'optional', label: 'Optional Data', completed: !!parsedData.personalInfo.phone }
    ];
    return steps;
  };

  const renderProgressBar = () => {
    const steps = getProgressSteps();
    const completedCount = steps.filter(step => step.completed).length;
    const progressPercentage = (completedCount / steps.length) * 100;
    
    return (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Onboarding Progress</h3>
          <div className="text-sm text-gray-600">
            {completedCount} of {steps.length} completed
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
        
        {/* Steps */}
        <div className="flex items-center justify-between relative">
          {steps.map((step, index) => (
            <div key={step.id} className="flex flex-col items-center relative z-10">
              {/* Step Circle */}
              <div className={`relative flex items-center justify-center w-12 h-12 rounded-full transition-all duration-500 transform ${
                step.completed 
                  ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg scale-110' 
                  : 'bg-white border-2 border-gray-300 text-gray-500'
              }`}>
                {step.completed ? (
                  <FaCheckCircle className="text-lg" />
                ) : (
                  <span className="text-sm font-semibold">{index + 1}</span>
                )}
                
                {/* Pulse animation for current step */}
                {!step.completed && index === completedCount && (
                  <div className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-20" />
                )}
              </div>
              
              {/* Step Label */}
              <div className="mt-3 text-center">
                <div className={`text-sm font-medium transition-colors duration-300 ${
                  step.completed 
                    ? 'text-green-600' 
                    : index === completedCount 
                    ? 'text-blue-600' 
                    : 'text-gray-500'
                }`}>
                  {step.label}
                </div>
                <div className={`text-xs mt-1 transition-colors duration-300 ${
                  step.completed 
                    ? 'text-green-500' 
                    : index === completedCount 
                    ? 'text-blue-500' 
                    : 'text-gray-400'
                }`}>
                  {step.completed ? 'Complete' : index === completedCount ? 'Current' : 'Pending'}
                </div>
              </div>
              
              {/* Connecting Line */}
              {index < steps.length - 1 && (
                <div className="absolute top-6 left-1/2 w-full h-0.5 bg-gray-200 -z-10">
                  <div className={`h-full transition-all duration-500 ${
                    step.completed 
                      ? 'bg-gradient-to-r from-green-500 to-green-400' 
                      : 'bg-gray-200'
                  }`} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCollectedData = () => {
    // Only show meaningful data - filter out placeholder or incomplete data
    const hasEmail = parsedData.email && parsedData.email.includes('@') && parsedData.email.length > 5;
    const hasFirstName = parsedData.personalInfo.firstName && parsedData.personalInfo.firstName.length > 1 && !parsedData.personalInfo.firstName.match(/^(here|test|demo|platform)$/i);
    const hasLastName = parsedData.personalInfo.lastName && parsedData.personalInfo.lastName.length > 1 && !parsedData.personalInfo.lastName.match(/^(here|test|demo|platform)$/i);
    const hasCompany = parsedData.companyInfo.name && parsedData.companyInfo.name.length > 3 && !parsedData.companyInfo.name.match(/^(platform|here|test|demo)$/i);
    const hasPhone = parsedData.personalInfo.phone && parsedData.personalInfo.phone.length > 5;
    const hasVatId = parsedData.companyInfo.vatId && parsedData.companyInfo.vatId.length > 3;
    
    const hasData = hasEmail || hasFirstName || hasLastName || hasCompany || hasPhone || hasVatId;
    if (!hasData) return null;

    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
          <FaCheckCircle className="mr-2" />
          Collected Information
        </h3>
        
        <div className="space-y-4">
          {hasEmail && (
            <div className="flex items-center">
              <FaEnvelope className="text-blue-600 mr-3" />
              <div>
                <span className="text-sm font-medium text-gray-700">Email:</span>
                <span className="ml-2 text-sm text-gray-900">{parsedData.email}</span>
              </div>
            </div>
          )}
          
          {hasFirstName && (
            <div className="flex items-center">
              <FaUser className="text-blue-600 mr-3" />
              <div>
                <span className="text-sm font-medium text-gray-700">First Name:</span>
                <span className="ml-2 text-sm text-gray-900">{parsedData.personalInfo.firstName}</span>
              </div>
            </div>
          )}
          
          {hasLastName && (
            <div className="flex items-center">
              <FaUser className="text-blue-600 mr-3" />
              <div>
                <span className="text-sm font-medium text-gray-700">Last Name:</span>
                <span className="ml-2 text-sm text-gray-900">{parsedData.personalInfo.lastName}</span>
              </div>
            </div>
          )}
          
          {hasPhone && (
            <div className="flex items-center">
              <FaPhone className="text-blue-600 mr-3" />
              <div>
                <span className="text-sm font-medium text-gray-700">Phone:</span>
                <span className="ml-2 text-sm text-gray-900">{parsedData.personalInfo.phone}</span>
              </div>
            </div>
          )}
          
          {hasCompany && (
            <div className="flex items-center">
              <FaBuilding className="text-blue-600 mr-3" />
              <div>
                <span className="text-sm font-medium text-gray-700">Company:</span>
                <span className="ml-2 text-sm text-gray-900">{parsedData.companyInfo.name}</span>
              </div>
            </div>
          )}
          
          {hasVatId && (
            <div className="flex items-center">
              <FaIdCard className="text-blue-600 mr-3" />
              <div>
                <span className="text-sm font-medium text-gray-700">VAT-ID:</span>
                <span className="ml-2 text-sm text-gray-900">{parsedData.companyInfo.vatId}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderNextSteps = () => {
    const hasNextSteps = parsedData.nextSteps.mandatory.length > 0 || parsedData.nextSteps.optional.length > 0;
    if (!hasNextSteps) return null;

    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-yellow-900 mb-4 flex items-center">
          <FaArrowRight className="mr-2" />
          Next Steps
        </h3>
        
        <div className="space-y-4">
          {parsedData.nextSteps.mandatory.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-red-700 mb-2">Required:</h4>
              <ul className="space-y-2">
                {parsedData.nextSteps.mandatory.map((step, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span className="text-sm text-gray-800">{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {parsedData.nextSteps.optional.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Optional:</h4>
              <ul className="space-y-2">
                {parsedData.nextSteps.optional.map((step, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-gray-500 mr-2">•</span>
                    <span className="text-sm text-gray-600">{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAIMessage = () => {
    if (!parsedData.aiMessage && !parsedData.title) return null;

    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Message</h3>
        <div className="text-sm text-gray-800">
          {parse((parsedData.aiMessage || message.text).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'))}
        </div>
      </div>
    );
  };

  const renderPersonalInfo = () => {
    if (!parsedData.personalInfo.firstName && !parsedData.personalInfo.lastName) return null;

    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
            <FaUser className="text-blue-600 text-lg" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-blue-900">Personal Information</h3>
            <p className="text-sm text-blue-700">Your personal details</p>
          </div>
        </div>
        
        <div className="space-y-3">
          {parsedData.personalInfo.firstName && (
            <div className="flex items-center">
              <FaUser className="text-blue-600 mr-3" />
              <div>
                <span className="text-sm font-medium text-gray-700">First Name:</span>
                <span className="ml-2 text-sm text-gray-900">{parsedData.personalInfo.firstName}</span>
              </div>
            </div>
          )}
          
          {parsedData.personalInfo.lastName && (
            <div className="flex items-center">
              <FaUser className="text-blue-600 mr-3" />
              <div>
                <span className="text-sm font-medium text-gray-700">Last Name:</span>
                <span className="ml-2 text-sm text-gray-900">{parsedData.personalInfo.lastName}</span>
              </div>
            </div>
          )}
          
          {parsedData.personalInfo.phone && (
            <div className="flex items-center">
              <FaPhone className="text-blue-600 mr-3" />
              <div>
                <span className="text-sm font-medium text-gray-700">Phone Number:</span>
                <span className="ml-2 text-sm text-gray-900">{parsedData.personalInfo.phone}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCompanyInfo = () => {
    if (!parsedData.companyInfo.name) return null;

    return (
      <div className="bg-green-50 rounded-lg p-4 mb-4">
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
            <FaCheckCircle className="text-green-600 text-lg" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Company Verified</h3>
            <p className="text-sm text-gray-600">Your company details have been verified</p>
          </div>
        </div>
        
        <div className="space-y-3">
          {parsedData.companyInfo.name && (
            <div className="flex items-start">
              <FaBuilding className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900">Company Name</p>
                <p className="text-sm text-gray-600">{parsedData.companyInfo.name}</p>
              </div>
            </div>
          )}
          
          {parsedData.companyInfo.vatId && (
            <div className="flex items-start">
              <FaIdCard className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900">VAT-ID</p>
                <p className="text-sm text-gray-600 font-mono">{parsedData.companyInfo.vatId}</p>
              </div>
            </div>
          )}
          
          {parsedData.companyInfo.address && (
            <div className="flex items-start">
              <FaMapMarkerAlt className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900">Address</p>
                <p className="text-sm text-gray-600">{parsedData.companyInfo.address}</p>
              </div>
            </div>
          )}
          
          {parsedData.companyInfo.email && (
            <div className="flex items-start">
              <FaEnvelope className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900">Business Email</p>
                <p className="text-sm text-gray-600">{parsedData.companyInfo.email}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };


  const renderAccountCredentials = () => {
    // Check if this is an account creation success message
    const hasCredentials = parsedData.title?.toLowerCase().includes('account created') || 
                          parsedData.title?.toLowerCase().includes('successfully') ||
                          (message.text.toLowerCase().includes('account created') && 
                           (message.text.toLowerCase().includes('username') || 
                            message.text.toLowerCase().includes('password') ||
                            message.text.toLowerCase().includes('login credentials'))) ||
                          (message.text.toLowerCase().includes('login credentials') && 
                           message.text.toLowerCase().includes('username')) ||
                          (message.text.toLowerCase().includes('username') && 
                           message.text.toLowerCase().includes('password') &&
                           !message.text.toLowerCase().includes('confirm') &&
                           !message.text.toLowerCase().includes('review'));

    if (!hasCredentials) return null;

    // Extract credentials from the message
    const extractCredentials = (text) => {
      const credentials = {
        username: '',
        password: '',
        accountId: '',
        loginUrl: ''
      };

      const lines = text.split('\n');
      for (const line of lines) {
        // Clean function to remove markdown formatting and extra characters
        const cleanValue = (value) => {
          return value
            .replace(/\*\*/g, '') // Remove ** markdown
            .replace(/\[|\]/g, '') // Remove brackets
            .replace(/Login Link/g, '') // Remove placeholder text
            .trim();
        };

        if (line.includes('Username:') || line.includes('username:')) {
          credentials.username = cleanValue(line.split(':')[1] || '');
        } else if (line.includes('Password:') || line.includes('password:')) {
          credentials.password = cleanValue(line.split(':')[1] || '');
        } else if (line.includes('Account ID:') || line.includes('account id:')) {
          credentials.accountId = cleanValue(line.split(':')[1] || '');
        } else if (line.includes('Login URL:') || line.includes('login url:')) {
          credentials.loginUrl = cleanValue(line.split(':')[1] || '');
        }
      }

      return credentials;
    };

    const credentials = extractCredentials(message.text);

    // Handle automatic login
    const handleAutoLogin = async () => {
      if (!credentials.username || !credentials.password) {
        setLoginError('Username or password not available');
        return;
      }

      try {
        setIsLoggingIn(true);
        setLoginError(null);
        
        const userTenant = localStorage.getItem(TENANT);
        const user = await login(credentials.username, credentials.password, userTenant);
        syncAuth(user);
        
        // Navigate to home page after successful login
        navigate(`/${userTenant}`);
      } catch (error) {
        console.error('Auto-login failed:', error);
        setLoginError('Login failed. Please try manually.');
      } finally {
        setIsLoggingIn(false);
      }
    };

    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
            <FaCheckCircle className="text-green-600 text-lg" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-green-900">Account Created Successfully!</h3>
            <p className="text-sm text-green-700">Your B2B account has been created with the following credentials:</p>
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-4 border border-green-200">
          <div className="space-y-3">
            {credentials.accountId && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Account ID:</span>
                <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">{credentials.accountId}</span>
              </div>
            )}
            
            {credentials.username && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Username:</span>
                <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">{credentials.username}</span>
              </div>
            )}
            
            {credentials.password && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Temporary Password:</span>
                <span className="text-sm font-mono bg-yellow-100 px-2 py-1 rounded border border-yellow-300">{credentials.password}</span>
              </div>
            )}
            
            {credentials.loginUrl && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Login URL:</span>
                <a 
                  href={credentials.loginUrl} 
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {credentials.loginUrl}
                </a>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start">
            <div className="text-yellow-600 mr-2">⚠️</div>
            <div className="text-sm text-yellow-800">
              <strong>Important:</strong> Please save these credentials securely. You'll need to change your password on first login.
            </div>
          </div>
        </div>

        {loginError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <div className="text-red-600 mr-2">❌</div>
              <div className="text-sm text-red-800">
                <strong>Login Error:</strong> {loginError}
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 flex space-x-3">
          <button 
            onClick={handleAutoLogin}
            disabled={isLoggingIn || !credentials.username || !credentials.password}
            className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-center flex items-center justify-center"
          >
            {isLoggingIn ? (
              <>
                <FaSpinner className="animate-spin mr-2" />
                Logging in...
              </>
            ) : (
              <>
                <FaSignInAlt className="mr-2" />
                Auto Login
              </>
            )}
          </button>
          <button 
            onClick={() => navigator.clipboard.writeText(`${credentials.username}\n${credentials.password}`)}
            className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors font-medium text-center"
          >
            Copy Credentials
          </button>
        </div>
      </div>
    );
  };


  const renderActionForm = () => {
    if (parsedData.actions.length === 0) return null;

    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
            <FaUser className="text-gray-600 text-lg" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Complete Registration</h3>
            <p className="text-sm text-gray-600">Provide your personal details to continue</p>
          </div>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              First Name *
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your first name"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Name *
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your last name"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
              <span className="text-gray-500 font-normal"> (optional)</span>
            </label>
            <input
              type="tel"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your phone number"
            />
          </div>
          
          <button className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center">
            <FaCheckCircle className="mr-2" />
            Confirm & Create Account
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Progress Bar - Always show */}
      {renderProgressBar()}
      
      {/* Collected Data - Show when we have data */}
      {renderCollectedData()}
      
      {/* Next Steps - Show when there are steps to complete */}
      {renderNextSteps()}
      
      {/* Account Credentials - Show when account is created */}
      {renderAccountCredentials()}
      
      {/* AI Message - Show additional AI content */}
      {renderAIMessage()}
    </div>
  );
};

export default B2BOnboardingRenderer;
